import { useEffect, useState } from 'react'
import { UserPlus, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { kassaAPI, adminAPI } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

export default function KassaUserPushCard() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [feedback, setFeedback] = useState(null)

  const loadUsers = () => {
    setLoading(true)
    adminAPI.getUsers()
      .then(res => {
        const list = res.data.users || res.data || []
        setUsers(list.filter(u => !u.kassa_sync_id))
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadUsers() }, [])

  const push = async (userId, username) => {
    if (!confirm(`Maak kassa-customer aan voor "${username}"?\n\nNaam & username worden overgenomen zoals ze in lanserver staan.`)) return
    setBusyId(userId)
    setFeedback(null)
    try {
      const res = await kassaAPI.pushUser(userId)
      setFeedback({ ok: true, message: `${username} → kassa-customer aangemaakt (sync_id ${res.data.kassaSyncId?.slice(0, 8)}...)` })
      loadUsers()
    } catch (err) {
      const data = err.response?.data
      setFeedback({ ok: false, message: `${username}: ${data?.error || err.message}${data?.upstream?.errors ? ' — ' + JSON.stringify(data.upstream.errors) : ''}` })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="p-3 rounded-lg border border-border/50 bg-card/50 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <UserPlus className="h-4 w-4 text-neon-blue" />
          Users zonder kassa-koppeling
        </div>
        <Badge variant="outline">{loading ? '…' : users.length}</Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : users.length === 0 ? (
        <p className="text-xs text-muted-foreground">Alle users hebben een kassa-koppeling. 🎉</p>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between gap-2 p-2 rounded border border-border/40 bg-background/50">
              <div className="flex-1 min-w-0 text-sm">
                <p className="truncate font-medium">{u.display_name || u.username}</p>
                <p className="text-[10px] text-muted-foreground">
                  @{u.username} · {u.clan_uid ? `RFID ${u.clan_uid}` : 'geen RFID'}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => push(u.id, u.username)} disabled={busyId !== null}>
                {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Push naar kassa'}
              </Button>
            </div>
          ))}
        </div>
      )}

      {feedback && (
        <div className={`flex items-start gap-2 text-xs p-2 rounded border ${feedback.ok ? 'border-green-500/30 bg-green-500/5 text-green-600' : 'border-destructive/30 bg-destructive/5 text-destructive'}`}>
          {feedback.ok ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
          <span>{feedback.message}</span>
        </div>
      )}
    </div>
  )
}
